// src/pages/seeker/SeekerQualifications.jsx

import React, { useEffect, useState, useRef } from 'react';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { X, Check, Loader, Copy, Trash2, MoreVertical, GraduationCap, MapPin, Calendar, Atom, Pencil, Sparkles, ChevronUp, ChevronDown } from 'lucide-react';
import { FieldError, useConfirm, SkillExtractModal, AddBtn, DelBtn, usePersistedState } from '../../components/shared/UI';

const C = {
  navy: '#0e4e95', blue: '#3D80CC', white: '#ffffff',
  offwhite: '#f4f7fb', text: '#1a1a1a', muted: '#666',
  border: '#d0daea',
};

const FL = ({ label, required, children, hint }) => (
  <div style={{ marginBottom: '14px' }}>
    <label className="form-label">{label}{required && <span style={{ color: '#CC0000', marginLeft: '2px' }}>*</span>}</label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>{hint}</div>}
  </div>
);

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const QUAL_LEVELS = [
  'High School Certificate',
  'Diploma',
  'Advanced Diploma',
  'Graduate Certificate',
  'Graduate Diploma',
  'Bachelor Degree',
  'Master Degree',
  'Doctorate',
];

const NOW       = new Date();
const NOW_YEAR  = NOW.getFullYear();
const NOW_MONTH = NOW.getMonth() + 1; // 1-12
const YEARS = Array.from({ length: 60 }, (_, i) => NOW_YEAR - i);

const formatQualDate = (year, month) => {
  if (!year) return null;
  if (!month) return String(year);
  return `${MONTHS[parseInt(month) - 1].slice(0, 3)} ${year}`;
};

// Convert API record → form shape
const qualFromApi = (q) => ({
  id:             q.id,
  level:          q.degree        || '',
  fieldOfStudy:   q.fieldOfStudy  || '',
  institution:    q.institution   || '',
  enrolledYear:   q.startYear     ? String(q.startYear)  : '',
  enrolledMonth:  q.startMonth    ? String(q.startMonth) : '',
  graduatedYear:  q.endYear       ? String(q.endYear)    : '',
  graduatedMonth: q.endMonth      ? String(q.endMonth)   : '',
  units:          Array.isArray(q.units) ? q.units : [],
});

// Convert form shape → API payload
const qualToApi = (form) => ({
  degree:       form.level,
  fieldOfStudy: form.fieldOfStudy.trim(),
  institution:  form.institution?.trim() || null,
  isCompleted:  !!form.graduatedYear,
  startYear:    form.enrolledYear    ? parseInt(form.enrolledYear)    : null,
  startMonth:   form.enrolledYear && form.enrolledMonth ? parseInt(form.enrolledMonth) : null,
  endYear:      form.graduatedYear   ? parseInt(form.graduatedYear)   : null,
  endMonth:     form.graduatedYear && form.graduatedMonth ? parseInt(form.graduatedMonth) : null,
  // units may be string[] (from loaded state) or { id, text }[] (from edit form)
  units:        (form.units || []).map(u => typeof u === 'string' ? u : u.text).filter(Boolean),
});

const emptyQual = () => ({
  id:             crypto.randomUUID(),
  level:          '',
  fieldOfStudy:   '',
  institution:    '',
  enrolledYear:   '',
  enrolledMonth:  '',
  graduatedYear:  '',
  graduatedMonth: '',
  units:          [],
  _isNew:         true,
});

// ── Card menu ──────────────────────────────────────────
const CardMenu = ({ onEdit, onDuplicate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const Item = ({ onClick, icon, label, danger }) => (
    <button onClick={() => { onClick(); setOpen(false); }}
      style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 14px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', color: danger ? 'var(--danger)' : 'var(--text)', borderBottom: danger ? 'none' : `1px solid ${C.border}` }}
      onMouseEnter={e => e.currentTarget.style.background = danger ? '#fff5f5' : C.offwhite}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
      {icon} {label}
    </button>
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', display: 'flex', padding: '4px', borderRadius: '3px', flexShrink: 0 }}
        onMouseEnter={e => e.currentTarget.style.color = '#fff'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}>
        <MoreVertical size={16} />
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 9999, background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 4px 16px rgba(0,0,0,0.18)', minWidth: '140px', overflow: 'hidden' }}>
          <Item onClick={onEdit}      icon={<Pencil size={13} />}  label="Edit" />
          <Item onClick={onDuplicate} icon={<Copy size={13} />}    label="Duplicate" />
          <Item onClick={onDelete}    icon={<Trash2 size={13} />}  label="Delete" danger />
        </div>
      )}
    </div>
  );
};

// ── Display mode ───────────────────────────────────────
const QualDisplay = ({ qual, onEdit, onDuplicate, onDelete, onExtract, isExtracting }) => {
  const enrolledStr  = formatQualDate(qual.enrolledYear,  qual.enrolledMonth);
  const graduatedStr = formatQualDate(qual.graduatedYear, qual.graduatedMonth);
  const heading = [qual.level, qual.fieldOfStudy].filter(Boolean).join(' — ');
  const [open, setOpen] = usePersistedState(`tikhet:seeker:qual:${qual.id}:open`, true);

  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', marginBottom: '14px', overflow: 'visible' }}>
      <div
        style={{ padding: '8px 12px', background: '#4A6FA5', borderRadius: open ? '6px 6px 0 0' : '6px', display: 'flex', alignItems: 'center', gap: '10px', position: 'relative', cursor: 'pointer', userSelect: 'none' }}
        onClick={(e) => {
          if (e.target.closest('button, [data-cardmenu]')) return;
          setOpen(o => !o);
        }}
      >
        <span style={{ color: '#E8EDF2', fontSize: '15px', fontWeight: 800, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {heading || <span style={{ fontStyle: 'italic', opacity: 0.6, fontWeight: 400 }}>Untitled</span>}
        </span>
        <CardMenu onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
        {open ? <ChevronUp size={14} color="#E8EDF2" style={{ opacity: 0.85, flexShrink: 0 }} /> : <ChevronDown size={14} color="#E8EDF2" style={{ opacity: 0.85, flexShrink: 0 }} />}
      </div>

      {open && <>
      <div style={{ padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {qual.institution && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MapPin size={16} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '15px', color: C.text }}>{qual.institution}</span>
          </div>
        )}
        {(enrolledStr || graduatedStr) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Calendar size={16} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '15px', color: C.text }}>
              {enrolledStr && <span>Enrolled {enrolledStr}</span>}
              {enrolledStr && graduatedStr && <span style={{ color: C.muted, margin: '0 8px' }}>→</span>}
              {graduatedStr && <span>Graduated {graduatedStr}</span>}
            </span>
          </div>
        )}
        {qual.units.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <Atom size={16} color={C.muted} style={{ flexShrink: 0, marginTop: '3px' }} />
            <ul style={{ margin: 0, paddingLeft: 18, listStyleType: 'disc' }}>
              {qual.units.map((u, i) => (
                <li key={i} style={{ fontSize: 12, color: C.text, marginBottom: 3, lineHeight: 1.5 }}>{u}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Atom size={16} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '14px', color: C.muted, fontStyle: 'italic' }}>No core units added</span>
          </div>
        )}
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, padding: '7px 16px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onExtract}
          disabled={isExtracting}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: isExtracting ? C.muted : C.blue, background: 'none', border: 'none', cursor: isExtracting ? 'default' : 'pointer', padding: '2px 0', fontFamily: "'Exo 2', sans-serif" }}
        >
          {isExtracting
            ? <><Loader size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> Extracting…</>
            : <><Sparkles size={12} /> Extract Skills</>}
        </button>
      </div>
      </>}
    </div>
  );
};

// ── Date pair (year + month selects) ──────────────────
const DatePair = ({ yearValue, monthValue, onYearChange, onMonthChange, disabled }) => (
  <div style={{ display: 'flex', gap: '8px' }}>
    <select value={yearValue} onChange={e => onYearChange(e.target.value)} disabled={disabled} className="form-input" style={{ flex: 1 }}>
      <option value="">Year (optional)</option>
      {YEARS.map(y => <option key={y} value={String(y)}>{y}</option>)}
    </select>
    <select value={monthValue} onChange={e => onMonthChange(e.target.value)} disabled={disabled || !yearValue} className="form-input" style={{ flex: 1 }}>
      <option value="">Month (optional)</option>
      {MONTHS.map((m, i) => {
        const mn = i + 1;
        if (parseInt(yearValue) === NOW_YEAR && mn > NOW_MONTH) return null;
        return <option key={mn} value={String(mn)}>{m}</option>;
      })}
    </select>
  </div>
);

// ── Edit mode ──────────────────────────────────────────
const QualEdit = ({ qual, onSave, onCancel }) => {
  const [form,    setForm]    = useState({
    ...qual,
    // Convert string[] from API into { id, text }[] for numbered-input editing
    units: (qual.units || []).map(text => ({ id: crypto.randomUUID(), text })),
  });
  const [saving,  setSaving]  = useState(false);
  const [errors,  setErrors]  = useState({});

  const unitRefs       = useRef({});
  const pendingFocusId = useRef(null);

  useEffect(() => {
    if (pendingFocusId.current && unitRefs.current[pendingFocusId.current]) {
      unitRefs.current[pendingFocusId.current].focus();
      pendingFocusId.current = null;
    }
  });

  const addUnit = () => {
    const newUnit = { id: crypto.randomUUID(), text: '' };
    setForm(f => ({ ...f, units: [...f.units, newUnit] }));
    pendingFocusId.current = newUnit.id;
  };

  const removeUnit = (id) => setForm(f => ({ ...f, units: f.units.filter(u => u.id !== id) }));

  const updateUnit = (id, text) => setForm(f => ({ ...f, units: f.units.map(u => u.id === id ? { ...u, text } : u) }));

  const handleSave = async () => {
    const errs = {};
    if (!form.level)                errs.level       = 'Qualification level is required';
    if (!form.fieldOfStudy.trim())  errs.fieldOfStudy = 'Field of study is required';
    if (form.enrolledMonth  && !form.enrolledYear)  errs.enrolledYear  = 'Year required when month is selected';
    if (form.graduatedMonth && !form.graduatedYear) errs.graduatedYear = 'Year required when month is selected';
    if (form.enrolledYear) {
      const ey = parseInt(form.enrolledYear), em = parseInt(form.enrolledMonth) || 0;
      if (ey > NOW_YEAR || (ey === NOW_YEAR && em > NOW_MONTH)) errs.enrolledYear = 'Enrolled date cannot be in the future';
    }
    if (form.graduatedYear) {
      const gy = parseInt(form.graduatedYear), gm = parseInt(form.graduatedMonth) || 0;
      if (gy > NOW_YEAR || (gy === NOW_YEAR && gm > NOW_MONTH)) errs.graduatedYear = 'Graduated date cannot be in the future';
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ background: C.white, border: `2px solid ${C.blue}`, borderRadius: '6px', marginBottom: '14px', overflow: 'visible' }}>
      <div style={{ background: '#f0f6ff', padding: '10px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Editing Educational Experience</span>
      </div>

      <div style={{ padding: '16px' }}>
        <FL label="Qualification Level" required>
          <select
            value={form.level}
            onChange={e => { setForm(f => ({ ...f, level: e.target.value })); setErrors(er => ({ ...er, level: '' })); }}
            className="form-input"
            style={{ background: 'white' }}
          >
            <option value="">— Select —</option>
            {QUAL_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          {errors.level && <FieldError>{errors.level}</FieldError>}
        </FL>
        <FL label="Field of Study" required>
          <input value={form.fieldOfStudy} onChange={e => { setForm(f => ({ ...f, fieldOfStudy: e.target.value })); setErrors(er => ({ ...er, fieldOfStudy: '' })); }} placeholder="e.g. Accounting, Computer Science" className="form-input" />
          {errors.fieldOfStudy && <FieldError>{errors.fieldOfStudy}</FieldError>}
        </FL>
        <FL label="Institution">
          <input value={form.institution} onChange={e => setForm(f => ({ ...f, institution: e.target.value }))} placeholder="e.g. University of Sydney" className="form-input" />
        </FL>

        <div style={{ marginBottom: '14px' }}>
          <label className="form-label">Enrolled</label>
          <DatePair
            yearValue={form.enrolledYear}
            monthValue={form.enrolledMonth}
            onYearChange={y => setForm(f => ({ ...f, enrolledYear: y, enrolledMonth: y ? f.enrolledMonth : '' }))}
            onMonthChange={m => setForm(f => ({ ...f, enrolledMonth: m }))}
          />
          {errors.enrolledYear && <FieldError>{errors.enrolledYear}</FieldError>}
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label className="form-label">Graduated</label>
          <DatePair
            yearValue={form.graduatedYear}
            monthValue={form.graduatedMonth}
            onYearChange={y => setForm(f => ({ ...f, graduatedYear: y, graduatedMonth: y ? f.graduatedMonth : '' }))}
            onMonthChange={m => setForm(f => ({ ...f, graduatedMonth: m }))}
          />
          {errors.graduatedYear && <FieldError>{errors.graduatedYear}</FieldError>}
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Core Units</label>
            <AddBtn onClick={addUnit} size={11} title="Add Unit" />
          </div>
          {form.units.map((unit, idx) => (
            <div key={unit.id} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, width: 16, textAlign: 'right' }}>{idx + 1}.</span>
              <input
                ref={el => { if (el) unitRefs.current[unit.id] = el; else delete unitRefs.current[unit.id]; }}
                className="form-input"
                value={unit.text}
                onChange={e => updateUnit(unit.id, e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addUnit(); } }}
                placeholder="e.g. Financial Accounting…"
                maxLength={100}
                style={{ flex: 1, fontSize: 12 }}
              />
              <DelBtn onClick={() => removeUnit(unit.id)} size={12} />
            </div>
          ))}
          {form.units.length === 0 && (
            <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', marginTop: 2 }}>
              No core units added yet. Click "Add Unit" to list subjects studied.
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '8px', background: C.offwhite }}>
        <button onClick={onCancel} disabled={saving} className="btn btn-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <X size={13} /> Cancel
        </button>
        <button onClick={handleSave} disabled={saving} className="btn btn-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {saving ? <><Loader size={13} /> Saving…</> : <><Check size={13} /> Save</>}
        </button>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════
export const SeekerQualifications = () => {
  const { confirm, dialog } = useConfirm();
  const [quals,        setQuals]        = useState([]);
  const [editing,      setEditing]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [extracting,   setExtracting]   = useState(null); // ID of card being extracted
  const [extractModal, setExtractModal] = useState(null); // { sourceLabel, skills, profileSkillNames }
  const [skillCache,   setSkillCache]   = useState({});   // { [qualId]: string[] } — cleared on save

  const load = async () => {
    try {
      const res = await seekerAPI.getEducation();
      setQuals((res.data.data || []).map(qualFromApi));
    } catch { toast.error('Failed to load qualifications'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSaveCard = async (form) => {
    const payload = qualToApi(form);
    try {
      if (form._isNew) {
        await seekerAPI.addEducation(payload);
      } else {
        await seekerAPI.updateEducation(form.id, payload);
        setSkillCache(prev => { const next = { ...prev }; delete next[form.id]; return next; });
      }
      await load();
      setEditing(null);
      toast.success('Qualification saved');
    } catch { /* interceptor toasts the error */ }
  };

  const handleCancelCard = (id) => {
    const q = quals.find(q => q.id === id);
    if (q?._isNew) setQuals(prev => prev.filter(q => q.id !== id));
    setEditing(null);
  };

  const handleDuplicate = async (id) => {
    const q = quals.find(q => q.id === id);
    if (!q) return;
    try {
      await seekerAPI.addEducation(qualToApi(q));
      await load();
      toast.success('Qualification duplicated');
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleDelete = async (id) => {
    if (!await confirm('Delete Qualification', 'Are you sure you want to delete this qualification?')) return;
    try {
      await seekerAPI.deleteEducation(id);
      setQuals(prev => prev.filter(q => q.id !== id));
      if (editing === id) setEditing(null);
      toast.success('Qualification deleted');
    } catch { toast.error('Failed to delete qualification'); }
  };

  const handleAddNew = () => {
    const q = emptyQual();
    setQuals(prev => [...prev, q]);
    setEditing(q.id);
  };

  const handleExtract = async (qual) => {
    setExtracting(qual.id);

    // Phase 1: check for existing links from this source
    let existingLinkCount = 0;
    try {
      const derivationsRes = await seekerAPI.getSkillDerivations();
      existingLinkCount = (derivationsRes.data.data?.educationLinks || [])
        .filter(l => l.educationId === qual.id).length;
    } catch { setExtracting(null); return; }

    // Warn and clear if re-extracting
    let cacheCleared = false;
    if (existingLinkCount > 0) {
      setExtracting(null);
      const ok = await confirm(
        'Re-extract Skills?',
        `This entry already has ${existingLinkCount} skill association${existingLinkCount !== 1 ? 's' : ''} recorded.\n\nRe-extracting will remove all of them. Skills with no other source will also be removed from your profile.\n\nThis cannot be undone.`,
        { confirmLabel: 'Yes, Re-extract', cancelLabel: 'Cancel' }
      );
      if (!ok) return;
      setExtracting(qual.id);
      try {
        const clearRes = await seekerAPI.clearSourceLinks({ sourceType: 'education', sourceId: qual.id });
        const { skillsRemoved } = clearRes.data.data || {};
        if (skillsRemoved > 0) toast.success(`${skillsRemoved} skill${skillsRemoved !== 1 ? 's' : ''} removed from your profile`);
      } catch { setExtracting(null); return; }
      setSkillCache(prev => { const next = { ...prev }; delete next[qual.id]; return next; });
      cacheCleared = true;
    }

    // Phase 2: extract and open modal
    try {
      const hasCached = !cacheCleared && (qual.id in skillCache);
      const [profileRes, extractRes] = await Promise.all([
        seekerAPI.getProfile(),
        hasCached ? Promise.resolve(null) : seekerAPI.extractSkills({ sourceType: 'education', sourceId: qual.id }),
      ]);
      const skills = hasCached ? skillCache[qual.id] : (extractRes.data.data?.skills || []);
      if (!hasCached) setSkillCache(prev => ({ ...prev, [qual.id]: skills }));

      const profileSkillMap = {};
      for (const jss of profileRes.data.data?.skills || []) {
        profileSkillMap[jss.skill.displayName.toLowerCase()] = jss.id;
      }

      setExtractModal({
        sourceId: qual.id,
        sourceType: 'education',
        sourceLabel: [qual.level, qual.fieldOfStudy].filter(Boolean).join(' — '),
        skills,
        alreadyLinkedSkillNames: [], // always empty here — either first extraction or just cleared
        profileSkillMap,
      });
    } catch { /* interceptor toasts */ }
    finally { setExtracting(null); }
  };

  const handleAddExtractedSkills = async (skillNames) => {
    const { sourceId, sourceType, profileSkillMap } = extractModal;
    let addedCount = 0;
    for (const name of skillNames) {
      let jobSeekerSkillId;
      try {
        const res = await seekerAPI.addSkill({ displayName: name });
        jobSeekerSkillId = res.data.data.id;
        addedCount++;
      } catch {
        jobSeekerSkillId = profileSkillMap[name.toLowerCase()];
      }
      if (jobSeekerSkillId) {
        try {
          if (sourceType === 'education') {
            await seekerAPI.addEducationLink({ jobSeekerSkillId, educationId: sourceId, sourceType: 'EXTRACTED' });
          } else {
            await seekerAPI.addExperienceLink({ jobSeekerSkillId, workExperienceId: sourceId, sourceType: 'EXTRACTED' });
          }
        } catch { /* upsert handles duplicates; silently ignore other errors */ }
      }
    }
    if (addedCount > 0) toast.success(`${addedCount} skill${addedCount !== 1 ? 's' : ''} added to your profile`);
    setExtractModal(null);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div className="fade-in">
      {dialog}
      <SkillExtractModal
        open={!!extractModal}
        onClose={() => setExtractModal(null)}
        sourceLabel={extractModal?.sourceLabel}
        suggestedSkills={extractModal?.skills || []}
        alreadyLinkedSkillNames={extractModal?.alreadyLinkedSkillNames || []}
        onAdd={handleAddExtractedSkills}
      />
      <div className="page-header">
        <h1>Education</h1>
        <p>Your academic and professional qualifications — used across all your CVs</p>
      </div>

      <div className="page-content">
        <div style={{ marginBottom: '16px' }}>
          {quals.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: C.muted }}>
              <GraduationCap size={36} color={C.border} style={{ marginBottom: '10px' }} />
              <p style={{ fontSize: '13px', margin: 0 }}>No qualifications added yet</p>
            </div>
          )}

          {quals.map(q =>
            editing === q.id ? (
              <QualEdit key={q.id} qual={q} onSave={handleSaveCard} onCancel={() => handleCancelCard(q.id)} />
            ) : (
              <QualDisplay
                key={q.id}
                qual={q}
                onEdit={() => setEditing(q.id)}
                onDuplicate={() => handleDuplicate(q.id)}
                onDelete={() => handleDelete(q.id)}
                onExtract={() => handleExtract(q)}
                isExtracting={extracting === q.id}
              />
            )
          )}

          <AddBtn onClick={handleAddNew} size={15} style={{ marginTop: quals.length > 0 ? '4px' : '0' }} title="Add Qualification" />
        </div>
      </div>
    </div>
  );
};
